import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PRICE_IDS } from "../types";
import { useAnalytics, AnalyticsEvents } from "@/hooks/useAnalytics";
import { useMetaPixel, MetaPixelEvents } from "@/hooks/useMetaPixel";
import { useLanguagePath } from "@/utils/language";

export const useSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { sendEvent } = useAnalytics();
  const { sendEvent: sendPixelEvent } = useMetaPixel();
  const { getPath } = useLanguagePath();

  const handleSubscription = async (priceId: string) => {
    try {
      setIsLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Necessário fazer login para continuar');
        return;
      }
      
      // Get current path including language prefix
      const currentPath = window.location.pathname;
      const returnUrl = getPath('/dashboard');
      
      // Obtemos o nome do plano e o número de alunos para o evento
      let planType = 'unknown';
      let planTier = 'unknown';
      let maxStudents = 0;
      let planPrice = 0;
      
      // Analisamos o priceId para extrair informações para o evento
      if (priceId.includes('pro_10')) {
        planType = 'pro';
        planTier = 'pro_10';
        maxStudents = 10;
        planPrice = 29.90;
      } else if (priceId.includes('pro_25')) {
        planType = 'pro';
        planTier = 'pro_25';
        maxStudents = 25;
        planPrice = 49.90;
      } else if (priceId.includes('pro_50')) {
        planType = 'pro';
        planTier = 'pro_50';
        maxStudents = 50;
        planPrice = 79.90;
      } else if (priceId.includes('premium')) {
        planType = 'premium';
        if (priceId.includes('premium_100')) {
          planTier = 'premium_100';
          maxStudents = 100;
          planPrice = 129.90;
        } else if (priceId.includes('premium_250')) {
          planTier = 'premium_250';
          maxStudents = 250;
          planPrice = 229.90;
        } else if (priceId.includes('premium_500')) {
          planTier = 'premium_500';
          maxStudents = 500;
          planPrice = 399.90;
        }
      }
      
      // Google Analytics: Evento de início de checkout
      sendEvent('begin_checkout', {
        event_category: 'subscription',
        subscription_plan: planType,
        subscription_tier: planTier,
        max_students: maxStudents,
        currency: 'EUR',
        value: planPrice
      });
      
      // Meta Pixel: Evento de início de checkout
      sendPixelEvent(MetaPixelEvents.INITIATE_CHECKOUT, {
        content_name: `${planType}_${planTier}`,
        content_category: 'subscription',
        content_type: 'product',
        value: planPrice,
        currency: 'EUR'
      });
      
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          priceId,
          userId: session.user.id,
          returnUrl,
          currentPath
        }
      });

      if (error || !data?.url) {
        console.error('Error creating checkout session:', error);
        toast.error('Erro ao processar pedido');
        
        // Google Analytics: Evento de erro no checkout
        sendEvent('checkout_error', {
          event_category: 'subscription',
          error_message: error.message
        });
        
        return;
      }

      if (data?.url) {
        // Google Analytics: Evento de checkout bem-sucedido e redirecionamento
        sendEvent(AnalyticsEvents.SUBSCRIPTION_PURCHASED, {
          event_category: 'subscription',
          event_label: `${planType}_${planTier}`,
          subscription_plan: planType,
          subscription_tier: planTier,
          max_students: maxStudents,
          value: planPrice
        });
        
        // Meta Pixel: Evento de checkout bem-sucedido e redirecionamento
        sendPixelEvent(MetaPixelEvents.SUBSCRIBE, {
          content_name: `${planType}_${planTier}`,
          content_category: 'subscription',
          content_type: 'product',
          value: planPrice,
          currency: 'EUR'
        });
        
        window.location.href = data.url;
      } else {
        toast.error('Erro ao redirecionar para checkout');
        
        // Google Analytics: Evento de erro no checkout
        sendEvent('checkout_error', {
          event_category: 'subscription',
          error_message: 'No checkout URL received'
        });
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao processar pedido');
      
      // Google Analytics: Evento de erro no checkout
      sendEvent('checkout_error', {
        event_category: 'subscription',
        error_message: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    handleSubscription
  };
};
